import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, User, Mail, Phone, MapPin, DollarSign, Building2,
  CalendarCheck, Zap, MessageSquare, Activity, Clock, Edit3, ChevronRight
} from "lucide-react";

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [aiMsg, setAiMsg] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  const fetchDetail = async () => {
    try {
      const { data: d } = await api.get(`/leads/${leadId}/detail`);
      setData(d);
      setEditForm({
        name: d.lead.name || "",
        email: d.lead.email || "",
        phone: d.lead.phone || "",
        status: d.lead.status || "new",
        notes: d.lead.notes || "",
        budget_min: d.lead.budget_min || 0,
        budget_max: d.lead.budget_max || 0,
        location_pref: d.lead.location_pref || "",
        property_type_pref: d.lead.property_type_pref || "",
      });
    } catch {
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [leadId]);

  const handleSave = async () => {
    try {
      await api.put(`/leads/${leadId}`, editForm);
      setEditMode(false);
      fetchDetail();
    } catch (err) {
      alert("Failed to update lead");
    }
  };

  const generateMessage = async (type) => {
    setShowAiModal(true);
    setAiLoading(true);
    try {
      const { data: d } = await api.post("/ai/generate-message", {
        context_type: type,
        lead_name: data.lead.name,
        additional_context: data.lead.notes || ""
      });
      setAiMsg(d.message);
    } catch {
      setAiMsg("Failed to generate message.");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-sm text-[#868E96]" data-testid="lead-detail-loading">Loading...</div>;
  if (!data) return null;

  const { lead, activities, matches, visits, agent } = data;
  const STATUSES = ["new", "contacted", "qualified", "visit_scheduled", "converted", "dormant", "lost"];

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/leads")} className="p-1.5 hover:bg-[#F1F3F5] rounded-sm" data-testid="back-to-leads">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-xl font-black text-[#0A0A0A]" style={{ fontFamily: 'Chivo' }}>{lead.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`status-badge status-${lead.status}`}>{lead.status?.replace("_", " ")}</span>
              <span className="text-xs text-[#868E96]">via {lead.source}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => generateMessage(lead.status === "dormant" ? "reactivation" : "reply")}
            className="h-9 px-3 border border-border text-sm font-medium hover:bg-[#F1F3F5] flex items-center gap-2 rounded-sm"
            data-testid="generate-message-btn"
          >
            <MessageSquare size={14} /> Generate Message
          </button>
          <button
            onClick={() => setEditMode(!editMode)}
            className="h-9 px-3 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] flex items-center gap-2 rounded-sm"
            data-testid="edit-lead-btn"
          >
            <Edit3 size={14} /> {editMode ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Lead Info */}
        <div className="lg:col-span-1 space-y-4">
          {/* Contact Info */}
          <div className="border border-border bg-white p-5">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-4">Contact</h3>
            {editMode ? (
              <div className="space-y-3">
                <InputField label="Name" value={editForm.name} onChange={v => setEditForm({...editForm, name: v})} testId="edit-name" />
                <InputField label="Email" value={editForm.email} onChange={v => setEditForm({...editForm, email: v})} testId="edit-email" />
                <InputField label="Phone" value={editForm.phone} onChange={v => setEditForm({...editForm, phone: v})} testId="edit-phone" />
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="edit-status">
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
                <button onClick={handleSave} className="w-full h-9 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="save-lead-btn">Save Changes</button>
              </div>
            ) : (
              <div className="space-y-3">
                <InfoRow icon={Mail} label="Email" value={lead.email || "—"} />
                <InfoRow icon={Phone} label="Phone" value={lead.phone || "—"} />
                <InfoRow icon={MapPin} label="Location" value={lead.location_pref || "—"} />
                <InfoRow icon={Building2} label="Type" value={lead.property_type_pref || "—"} />
                <InfoRow icon={DollarSign} label="Budget" value={lead.budget_max > 0 ? `$${(lead.budget_min || 0).toLocaleString()} - $${lead.budget_max.toLocaleString()}` : "—"} />
              </div>
            )}
          </div>

          {/* Agent */}
          {agent && (
            <div className="border border-border bg-white p-5">
              <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">Assigned Agent</h3>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#002FA7] rounded-sm flex items-center justify-center text-white text-sm font-bold">
                  {agent.name?.charAt(0) || "A"}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#0A0A0A]">{agent.name}</p>
                  <p className="text-xs text-[#868E96]">{agent.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="border border-border bg-white p-5">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">Notes</h3>
            {editMode ? (
              <textarea
                value={editForm.notes}
                onChange={e => setEditForm({...editForm, notes: e.target.value})}
                className="w-full h-24 p-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm resize-none"
                data-testid="edit-notes"
              />
            ) : (
              <p className="text-sm text-[#495057]">{lead.notes || "No notes."}</p>
            )}
          </div>
        </div>

        {/* Right column - Timeline & Data */}
        <div className="lg:col-span-2 space-y-4">
          {/* Matches */}
          <div className="border border-border bg-white p-5">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">
              Property Matches ({matches.length})
            </h3>
            {matches.length === 0 ? (
              <p className="text-sm text-[#868E96] py-2">No property matches yet.</p>
            ) : (
              <div className="space-y-2">
                {matches.map((m, i) => (
                  <div key={m.id || i} className="flex items-center justify-between py-2 border-b border-border last:border-0 animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }}>
                    <div className="flex items-center gap-3">
                      <Zap size={14} className={m.score >= 70 ? "text-[#2B8A3E]" : m.score >= 50 ? "text-[#E67700]" : "text-[#868E96]"} />
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A]">{m.property_title}</p>
                        <div className="flex gap-2 mt-0.5">
                          {m.score_breakdown && Object.entries(m.score_breakdown).map(([k, v]) => (
                            <span key={k} className="text-[10px] px-1 py-0.5 bg-[#F1F3F5] text-[#495057] border border-border">{k}: {v}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <span className={`text-sm font-bold ${m.score >= 70 ? 'text-[#2B8A3E]' : m.score >= 50 ? 'text-[#E67700]' : 'text-[#868E96]'}`}>
                      {m.score}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Visits */}
          <div className="border border-border bg-white p-5">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">
              Visits ({visits.length})
            </h3>
            {visits.length === 0 ? (
              <p className="text-sm text-[#868E96] py-2">No visits scheduled.</p>
            ) : (
              <div className="space-y-2">
                {visits.map((v, i) => (
                  <div key={v.id || i} className="flex items-center justify-between py-2 border-b border-border last:border-0 animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }}>
                    <div className="flex items-center gap-3">
                      <CalendarCheck size={14} className="text-[#002FA7]" />
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A]">{v.property_title}</p>
                        <p className="text-xs text-[#868E96]">{v.scheduled_at ? new Date(v.scheduled_at).toLocaleString() : ""}</p>
                      </div>
                    </div>
                    <span className={`status-badge status-${v.status}`}>{v.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="border border-border bg-white p-5">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-4">Activity Timeline</h3>
            {activities.length === 0 ? (
              <p className="text-sm text-[#868E96] py-2">No activity recorded.</p>
            ) : (
              <div className="relative">
                <div className="absolute left-[7px] top-0 bottom-0 w-px bg-border" />
                <div className="space-y-4">
                  {activities.map((a, i) => (
                    <div key={a.id || i} className="relative flex gap-4 pl-6 animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }}>
                      <div className="absolute left-0 top-1 w-[15px] h-[15px] bg-white border-2 border-[#002FA7] rounded-full flex items-center justify-center">
                        <div className="w-[5px] h-[5px] bg-[#002FA7] rounded-full" />
                      </div>
                      <div className="flex-1 pb-1">
                        <p className="text-sm text-[#0A0A0A]">{a.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock size={11} className="text-[#868E96]" />
                          <span className="text-xs text-[#868E96]">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#F1F3F5] text-[#495057] border border-border">{a.type}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Message Modal */}
      {showAiModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowAiModal(false)}>
          <div className="bg-white border border-border p-6 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="ai-detail-modal">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo' }}>Suggested Message</h3>
            {aiLoading ? (
              <p className="text-sm text-[#868E96] py-4">Generating...</p>
            ) : (
              <div>
                <textarea value={aiMsg} onChange={e => setAiMsg(e.target.value)} className="w-full h-32 p-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm resize-none" data-testid="ai-detail-textarea" />
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => setShowAiModal(false)} className="h-9 px-4 border border-border text-sm rounded-sm hover:bg-[#F1F3F5]">Close</button>
                  <button onClick={() => { navigator.clipboard.writeText(aiMsg); }} className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="copy-ai-msg-btn">Copy</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={14} className="text-[#868E96] shrink-0" />
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#868E96]">{label}</p>
        <p className="text-sm text-[#0A0A0A]">{value}</p>
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid={testId} />
    </div>
  );
}
